import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import ButtonCustom from 'src/components/form/Button'
import { formatNumPrice } from 'src/utils/helper'
import styled from 'styled-components'
import { getMenuById } from 'src/useCase/question'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store'
import LPHeader from 'src/components/layout/Header/LPHeader'
import { HeaderStyle } from 'src/components/layout/Header/HeaderStyle'
import ConverstationImageSrc from 'src/assets/images/landing/converstation.png'
import { PATH as PATH_KARTE_PLAN_PATH } from 'src/pages/kartePlan'
import { PATH as PATH_REGULAR } from 'src/pages/regular'
import { PATH as PATH_AUTOMATIC_PLAN } from 'src/pages/automaticPlan'
import { PATH as PATH_CHAT } from 'src/pages/chat/Chat'
import LocalStorage from 'src/utils/LocalStorage'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import usePlanSelection from 'src/hook/usePlanSelection'
import { getInfo } from 'src/useCase/user'
import { trackStep, JOURNEY_STEP } from 'src/utils/tracker'

export function ChooseServicePlanContainer() {
  const navigate = useNavigate()
  const user = useAppSelector((state) => state.ui.app.authInfo)
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const clinicId = user?.clinics?.[0]?.id ?? ''
  const menuId = searchParams.get('menu_id')
  const choice = searchParams.get('choice')
  const menuArray = useSelector(
    (state: any) => state.domain.automaticPlan?.menus?.entity ?? [],
  )
  const menu = menuArray[0] ?? {}
  const plans = menu?.plans ?? []

  const userTreatmentId = LocalStorage.getUserTreatmentId()
  const userTreatment = user?.user_treatments?.find(
    (t: any) => t.id === Number(userTreatmentId),
  )

  const { handlePlanSelection } = usePlanSelection(userTreatmentId)
  const loading = useAppSelector((state) => state.ui.app.loading)
  const [isFetched, setIsFetched] = React.useState(false)

  useEffect(() => {
    trackStep(JOURNEY_STEP.PLAN_VIEWED)
  }, [])

  useEffect(() => {
    if (menuId && clinicId) {
      const queryParams = { clinic_id: clinicId, with: 'plans' }
      dispatch(getMenuById({ menuId, params: queryParams }) as any)
    }
  }, [dispatch, menuId, clinicId])

  useEffect(() => {
    dispatch(
      getInfo({
        with: 'userTreatments',
      }),
    ).finally(() => {
      setIsFetched(true)
    })
  }, [dispatch])

  useEffect(() => {
    if (!isFetched) return
    if (loading) return
    if (!userTreatment) return

    switch (userTreatment.state) {
      case USER_TREATMENT_STATE.CONFIRMING_PRESCRIPTION:
        return navigate(
          PATH_KARTE_PLAN_PATH.PRESCRIPTION_PROPOSAL.replace(
            ':id',
            userTreatment.last_doctor_feedback?.id,
          ),
        )

      case USER_TREATMENT_STATE.DIRECT_PLAN_ELIGIBLE:
        return navigate(
          `${PATH_REGULAR.REGULAR_SUBSCRIPTION}?menu_id=${userTreatment.menu_id}`,
        )

      case USER_TREATMENT_STATE.CHAT_INTERVIEW_SCHEDULED:
      case USER_TREATMENT_STATE.CHAT_CONSULTATION_IN_PROGRESS:
        return navigate(PATH_CHAT)

      case USER_TREATMENT_STATE.AWAITING_PLAN:
        return

      default:
        return navigate(PATH_AUTOMATIC_PLAN.MEDICAL_EXAMINATION)
    }
  }, [isFetched, loading, userTreatment, navigate])

  return (
    <>
      <LPHeader />
      {choice === 'buy_plan' && (
        <WrapperHeader>
          <HeaderStyle>お決まりのプランをご選択ください</HeaderStyle>
          <HeaderStyle className="menu_name">診療科目：{menu.name}</HeaderStyle>
        </WrapperHeader>
      )}

      {choice === 'review_plans' && (
        <WrapperHeader>
          <HeaderStyle>
            ご希望のプランがあればご選択ください
            <br />
            なければ、医師の提案を待つをご選択ください
          </HeaderStyle>
          <HeaderStyle className="menu_name">診療科目：{menu.name}</HeaderStyle>
        </WrapperHeader>
      )}

      {plans && plans?.length > 0 && (
        <Wrapper className="w-full justify-center">
          {plans?.map((plan: any) => (
            <div className="plan" key={plan.id}>
              <div className="plan__header">
                <div className="plan__text">
                  <div className="plan__name">{plan.name}</div>
                  <div className="plan__tags">
                    {plan.tags?.map((item: string, index: number) => (
                      <div key={index} className="plan__tag">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="plan__detail">
                <div className="plan__wrapper">
                  <div className="d-flex gap-3 flex-column">
                    <div className="plan__infPlan d-flex gap-5 justify-content-center align-items-center">
                      <div className="plan__img rounded-circle">
                        <img
                          src={plan.image_url}
                          alt="Plan"
                          width={160}
                          height={160}
                          className="plan__imageDetail"
                        />
                      </div>

                      <div className="plan__price">
                        <div className="d-flex gap-1 align-items-center align-self-end flex-wrap">
                          <p className="plan__textTimes">初回</p>
                          <p className="plan__priceTimes">
                            ¥{formatNumPrice(plan.price_first)}
                          </p>
                        </div>
                        <div className="d-flex gap-1 align-items-center">
                          <p className="plan__textTimes">２回目以降</p>
                          <p className="plan__priceTimes">
                            ¥{formatNumPrice(plan.price_after)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="plan__descPlan d-flex gap-3 flex-column">
                      <div className="plan__detailDesc">
                        <div className="plan__detailDescTitle">薬の内訳</div>
                        <div className="plan__detailDescText">
                          {plan.medicines?.map((m: any) => m.name).join(' / ')}
                        </div>
                      </div>

                      <div className="plan__detailDesc">
                        <div className="plan__detailDescTitle">
                          期待できる効果・効能
                        </div>
                        <div className="plan__detailDescText">
                          {plan.treatment_policy}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center mt-30">
                    <ButtonCustom
                      className="plan__booking btn-default mg-auto"
                      loading={false}
                      showIconRight
                      onClick={() => handlePlanSelection({ planId: plan.id })}
                      name="このプランで申し込む"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Wrapper>
      )}

      {choice === 'review_plans' && (
        <WrapperFooter className="conversation__header">
          <HeaderStyle className="conversation-title">
            処方を受けたいプランが決まらない方はこちら
            <br />
            問診の内容をもとに、医師よりご提案します
          </HeaderStyle>
          <img src={ConverstationImageSrc} alt="Converstation" />
          <div className="d-flex justify-content-center mt-30">
            <ButtonCustom
              className="conversation__booking btn-default mg-auto"
              loading={false}
              name="医師の提案を待つ"
              onClick={() => handlePlanSelection({ waitDoctorProposal: true })}
            />
          </div>
        </WrapperFooter>
      )}
    </>
  )
}

const WrapperHeader = styled.div`
  padding: 0 15px;

  .menu_name {
    padding-top: 0;
    font-size: 15px;
    letter-spacing: normal;
  }
`

const Wrapper = styled.div`
  background-color: #f7f5ed;
  padding: 0 15px 60px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    padding: 30px 15px;
  }

  .wrapper__header {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-top: -48px;
    padding-top: 0;

    @media (max-width: 768px) {
      margin-top: -78px;
    }
  }

  .wrapper__text {
    text-align: center;
    line-height: 32px;
    font-size: 20px;
    font-weight: 700;
    color: black;
    letter-spacing: 0.1rem;

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  .plan {
    background-color: #fff;
    border: 1px solid #ccc;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__img {
      position: relative;
      z-index: 1;
      background-color: #f7f5ed;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 180px;
      height: 180px;
      @media (max-width: 768px) {
        width: 160px;
        height: 160px;
        object-fit: cover;
      }
    }

    &__imageDetail {
      @media (max-width: 768px) {
        width: 116px;
        height: 116px;
      }
    }

    &__text {
      padding: 16px 30px;
      background-color: #9e956f;
      display: flex;
      justify-content: space-between;
      gap: 20px;
      align-items: center;

      @media (max-width: 768px) {
        padding: 12px 20px;
        flex-direction: row;
        gap: 10px;
      }
    }

    &__name {
      font-size: 24px;
      font-weight: bold;
      color: white;
      width: 40%;

      @media (max-width: 768px) {
        font-size: 17px;
      }
    }

    &__tags {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      width: 60%;
      justify-content: end;

      .plan__tag {
        background-color: white;
        padding: 6px 8px;
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #9e956f;
        font-weight: bold;

        @media (max-width: 768px) {
          font-size: 13px;
          padding: 2px 7px;
        }
      }
    }

    &__detail {
      padding: 16px 30px;

      @media (max-width: 768px) {
        padding: 12px 15px;
      }
    }

    &__infPlan {
      flex-direction: row;
      align-items: center;

      @media (min-width: 769px) {
        flex-direction: row;
      }
    }

    &__price {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .plan__textTimes {
        font-size: 18px;
        font-weight: 700;
        color: #9e956f;

        @media (max-width: 768px) {
          font-size: 16px;
        }
      }

      .plan__priceTimes {
        font-size: 36px;
        font-weight: 600;
        color: black;

        @media (max-width: 768px) {
          font-size: 28px;
        }
      }
    }

    &__detailDesc {
      display: flex;
      flex-direction: column;
      gap: 10px;

      &Title {
        border: 1px solid #9e956f;
        padding: 4px 11px;
        width: fit-content;
        font-size: 17px;
        font-weight: 700;
        color: #9e956f;

        @media (max-width: 768px) {
          font-size: 14px;
        }
      }

      &Text {
        font-size: 13px;
        font-weight: 700;

        @media (max-width: 768px) {
          font-size: 12px;
        }
      }
    }

    &__booking {
      max-width: 360px;
      letter-spacing: 3px;
      background-color: #ce5848;
      &:hover {
        background-color: #ce5848 !important;
        opacity: 0.8 !important;
      }

      @media (max-width: 768px) {
        width: 100%;
      }
    }
  }
`

const WrapperFooter = styled.div`
  padding: 30px 15px 60px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  text-align: center;

  .conversation-title {
    font-weight: 700;
  }

  .conversation__booking {
    max-width: 360px;
    letter-spacing: 3px;
    background-color: #ce5848;
    &:hover {
      background-color: #ce5848 !important;
      opacity: 0.8 !important;
    }

    @media (max-width: 768px) {
      width: 100%;
    }
  }
`

export default ChooseServicePlanContainer
